import { ArrowRightOutlined, WarningFilled } from '@ant-design/icons'
import { Alert, Button, Space, Tag, Tooltip } from 'antd'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useIntercom } from 'react-use-intercom'

import { type ProductSourceFeed as ProductSourceFeedType } from '@DataManagement/types/product'
import { useGetFeedTargetForMeQuery } from '@Store/api/product'
import { useDataManagementState } from '@Store/slices/dataManagementSlice'
import getIntercomMsg from '@Utils/helpers/getIntercomMsg'

import RecentImports from '../../file-import/RecentImports'
import ProductSourceBodyWrapper from '../ProductSourceBodyWrapper'

const SYNC_PERIOD_LABELS: Record<string, string> = {
  DAILY: 'Every 24 hours',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
}

function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—'

  const parsed =
    typeof value === 'string'
      ? DateTime.fromISO(value)
      : DateTime.fromJSDate(new Date(value))

  return parsed.isValid ? parsed.toFormat('dd LLL yyyy HH:mm') : '—'
}

function SummaryRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 py-3 border-0 border-b border-solid border-gray-200 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="text-sm font-medium text-gray-700 sm:w-56 sm:shrink-0">
        {label}
      </span>
      <span className="min-w-0 text-sm text-gray-900 break-words">
        {children}
      </span>
    </div>
  )
}

/**
 * What a retailer sees of Feed Integration during the beta: their feed, not a
 * form for it.
 *
 * Setup and every change to it are done by support, so this panel offers no
 * controls at all — a feed URL, a schedule and a mapping the customer cannot
 * edit, and one route to changing any of it, which is Intercom. The wizard is
 * still the thing support uses, from a login-as session; the backend refuses
 * the writes for anyone else, so this is a matching face rather than the
 * enforcement.
 */
function FeedBetaPanel(): JSX.Element {
  const [showAllMappedFields, setShowAllMappedFields] = useState(false)

  const { isProductManagementAlreadySet, selectedProductSource } =
    useDataManagementState()
  const { showNewMessage } = useIntercom()

  // Same live read the wizard does, for the same reason: the run signals
  // (unmapped columns, skipped products, a sync in flight) only exist on
  // /conveyor/target/me, and polling is what makes them appear when the run
  // ends rather than on the next page load.
  const [isRunning, setIsRunning] = useState(false)
  const { data: liveFeedSource } = useGetFeedTargetForMeQuery(undefined, {
    skip: !isProductManagementAlreadySet,
    refetchOnMountOrArgChange: true,
    pollingInterval: isRunning ? 10_000 : 0,
  })

  const feedSource = {
    ...((selectedProductSource ?? {}) as ProductSourceFeedType),
    // `/conveyor/target/me` does not include the column mapping, so the
    // stored target's `conveyorTargetFields` survives this spread — the same
    // merge the wizard does.
    ...((liveFeedSource ?? {}) as Partial<ProductSourceFeedType>),
  } as ProductSourceFeedType

  const isSyncInProgress = !!feedSource?.isSyncInProgress

  // Poll only while a run is actually in flight, and stop as soon as it ends —
  // the last poll is the one that brings back the run's banners.
  useEffect(() => {
    setIsRunning(isSyncInProgress)
  }, [isSyncInProgress])

  const contactUs = (topic: string): void =>
    showNewMessage(getIntercomMsg('FeedIntegrationChange', { topic }))

  const requestFeedSetup = (): void =>
    showNewMessage(getIntercomMsg('FeedIntegrationRequest', {}))

  // Reached only if something other than the card put us here — the card
  // itself turns a click into an Intercom request when there is no feed, so
  // this is the fallback (a selection restored from state, say) rather than
  // the path a retailer normally takes.
  if (!isProductManagementAlreadySet) {
    return (
      <ProductSourceBodyWrapper title="Feed Integration">
        <div className="max-w-2xl">
          <p className="text-base text-gray-700">
            Feed Integration keeps your Prisync catalog in step with your own
            product feed: we read the file at a URL you host — XML, CSV, JSON or
            XLSX — and create, update and remove products from it on a schedule.
          </p>
          <p className="text-base text-gray-700">
            It is in beta, so our team sets it up with you rather than leaving
            you to it. Tell us where your feed lives and we will map its columns
            to Prisync&apos;s fields, agree a sync schedule with you, and run
            the first import together.
          </p>
          <Button
            type="primary"
            size="large"
            onClick={requestFeedSetup}
            className="mt-2 !px-5 !py-2.5 !leading-none border bg-primary-600 hover:!bg-primary-700 !border-primary-600 !text-white"
          >
            Contact us to get started
          </Button>
        </div>
      </ProductSourceBodyWrapper>
    )
  }

  const mappedFields = feedSource?.conveyorTargetFields ?? []
  const visibleMappedFields = showAllMappedFields
    ? mappedFields
    : mappedFields.slice(0, 4)

  const syncPeriod = feedSource?.syncPeriod
  const isScheduled = !!syncPeriod && syncPeriod !== 'NONE'
  const isPaused = feedSource?.isActive === 0

  return (
    <>
      <ProductSourceBodyWrapper title="Feed Integration">
        {(feedSource?.newUnmappedFields?.length ?? 0) > 0 && (
          <Alert
            type="info"
            showIcon
            className="mb-6 border border-solid border-blue-200 rounded-lg bg-blue-25"
            description={
              <span className="text-sm text-gray-700">
                Your feed has grown new columns that are not mapped yet:{' '}
                <strong>
                  {(feedSource.newUnmappedFields ?? []).join(', ')}
                </strong>
                .{' '}
                <button
                  type="button"
                  className="text-primary-600 font-medium bg-transparent border-0 p-0 cursor-pointer underline-offset-2 hover:underline"
                  onClick={() => contactUs('map the new columns in my feed')}
                >
                  Contact us
                </button>{' '}
                to map them.
              </span>
            }
          />
        )}
        {feedSource?.mineUrlDomainMismatch ? (
          <Alert
            type="warning"
            showIcon
            className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
            description={
              <span className="text-sm text-warning-700">
                The product URLs in your feed are on{' '}
                <strong>
                  {feedSource.mineUrlDomainMismatch.domains.join(', ')}
                </strong>
                {feedSource.mineUrlDomainMismatch.storeDomain ? (
                  <>
                    , but your store domain is set to{' '}
                    <strong>
                      {feedSource.mineUrlDomainMismatch.storeDomain}
                    </strong>
                  </>
                ) : (
                  ', but no store domain is set for your account'
                )}
                . {feedSource.mineUrlDomainMismatch.count}{' '}
                {feedSource.mineUrlDomainMismatch.count === 1
                  ? 'product is'
                  : 'products are'}{' '}
                being tracked as competitor URLs instead of your own, so their
                prices will not sync from your feed.{' '}
                <button
                  type="button"
                  className="text-primary-600 font-medium bg-transparent border-0 p-0 cursor-pointer underline-offset-2 hover:underline"
                  onClick={() => contactUs('correct my store domain')}
                >
                  Contact us
                </button>{' '}
                to correct your store domain.
              </span>
            }
          />
        ) : null}
        {feedSource?.stockUnresolved ? (
          <Alert
            type="warning"
            showIcon
            className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
            description={
              <span className="text-sm text-warning-700">
                We could not tell what some of the stock values in your feed
                mean, including{' '}
                <strong>{feedSource.stockUnresolved.values.join(', ')}</strong>.{' '}
                {feedSource.stockUnresolved.count}{' '}
                {feedSource.stockUnresolved.count === 1
                  ? 'product kept its'
                  : 'products kept their'}{' '}
                previous stock status instead.{' '}
                <button
                  type="button"
                  className="text-primary-600 font-medium bg-transparent border-0 p-0 cursor-pointer underline-offset-2 hover:underline"
                  onClick={() =>
                    contactUs('tell you what my stock values mean')
                  }
                >
                  Contact us
                </button>{' '}
                to tell us what they mean.
              </span>
            }
          />
        ) : null}
        {feedSource?.stockWithoutMineUrl ? (
          <Alert
            type="warning"
            showIcon
            className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
            description={
              <span className="text-sm text-warning-700">
                Your feed&rsquo;s stock column is mapped, but your product URL
                is not. Stock is stored against your own product URL, so the
                stock column is being ignored.{' '}
                <button
                  type="button"
                  className="text-primary-600 font-medium bg-transparent border-0 p-0 cursor-pointer underline-offset-2 hover:underline"
                  onClick={() => contactUs('map my product URL column')}
                >
                  Contact us
                </button>{' '}
                to map it.
              </span>
            }
          />
        ) : null}
        {(feedSource?.productLimitSkipped ?? 0) > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mb-6 border border-solid border-warning-300 rounded-lg bg-warning-25"
            description={
              <span className="text-sm text-warning-700">
                Product limit reached. {feedSource.productLimitSkipped}{' '}
                {feedSource.productLimitSkipped === 1 ? 'product' : 'products'}{' '}
                could not be added during the last sync.
              </span>
            }
          />
        )}
        <div className="flex flex-col">
          <SummaryRow label="Feed URL">{feedSource?.path ?? '—'}</SummaryRow>
          <SummaryRow label="Sync schedule">
            {isScheduled ? (
              (SYNC_PERIOD_LABELS[syncPeriod] ?? syncPeriod)
            ) : (
              <span className="inline-flex items-center gap-2">
                Off
                <Tooltip title="Your products no longer update automatically. Contact us to turn scheduled sync back on.">
                  <WarningFilled className="!text-warning-600 text-base" />
                </Tooltip>
              </span>
            )}
          </SummaryRow>
          <SummaryRow label="Status">
            {isSyncInProgress
              ? 'Sync in progress'
              : isPaused
                ? 'Paused'
                : 'Active'}
          </SummaryRow>
          <SummaryRow label="Last sync">
            {formatDateTime(feedSource?.lastSyncAt)}
          </SummaryRow>
          <SummaryRow label="Mapped properties">
            {mappedFields.length === 0 ? (
              '—'
            ) : (
              <Space wrap className="gap-2">
                {visibleMappedFields.map((f) => (
                  <Tag
                    key={f?.id ?? f?.name}
                    className="inline-flex items-center gap-1 m-0 rounded-[6px] px-2.5 py-1 text-xs leading-[18px] bg-white border-solid border-[#D0D5DD]"
                  >
                    <span className="text-[#667085]">{f?.name}</span>
                    <ArrowRightOutlined
                      className="text-[#98A2B3]"
                      width={12}
                      height={12}
                      aria-hidden
                    />
                    <span className="font-medium text-[#344054]">
                      {f?.conveyorField?.label}
                    </span>
                  </Tag>
                ))}
                {mappedFields.length > 4 && (
                  <Button
                    type="link"
                    className="!px-0 !py-0 !text-sm !leading-none !text-primary-600 hover:!bg-transparent gap-0"
                    onClick={() => setShowAllMappedFields(!showAllMappedFields)}
                  >
                    {showAllMappedFields
                      ? 'Show Less'
                      : `Show All Mappings (${mappedFields.length})`}
                  </Button>
                )}
              </Space>
            )}
          </SummaryRow>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-6">
          <span className="text-sm text-gray-500">
            Feed Integration is in beta, so our team manages it for you — the
            feed URL, its credentials, the schedule and the column mapping.
          </span>
          <Button
            size="large"
            onClick={() => contactUs('change my feed integration')}
            className="!px-5 !py-2.5 !leading-none border bg-white hover:!bg-gray-100 !border-gray-300 !text-gray-700"
          >
            Contact us to make a change
          </Button>
        </div>
      </ProductSourceBodyWrapper>
      <RecentImports type={['conveyor']} />
    </>
  )
}

export default FeedBetaPanel
